(() => {
  "use strict";
  function create(hooks) {
    const doc = hooks.document || document;
    const el = Object.fromEntries(["dialog", "title", "subject", "status", "error", "refresh", "list",
      "form", "name", "submit", "cancel-edit", "confirmation", "confirm-text", "delete", "keep", "apply"]
      .map(key => [key, doc.getElementById("saved-folder-" + key)]));
    const strip = doc.getElementById("save-folders");
    const owner = () => hooks.viewer()?.id || null;
    const scope = () => hooks.scope?.() ?? null, csrf = () => hooks.csrf?.() ?? null;
    let identity = owner(), accountScope = scope(), accountCsrf = csrf(), epoch = 0, request = 0, session = null;
    let model = {items:[]}, loading = false, listError = "";
    let mutationRevision = 0, refreshPending = false;
    const writes = new Set();
    const make = (tag, text = "", className = "") => {
      const node = doc.createElement(tag); node.textContent = text; node.className = className; return node;
    };
    function button(text, callback, className = "secondary-button") {
      const node = make("button", text, className); node.type = "button";
      node.addEventListener("click", callback); return node;
    }
    const accountCurrent = s => Boolean(s && s.owner === owner() && s.scope === scope()
      && s.csrf === csrf() && s.epoch === epoch);
    const current = s => session === s && accountCurrent(s) && el.dialog.open;
    const writable = s => current(s) && hooks.canWrite() && s.ready && !s.pending && !s.unconfirmed;
    function validate(data, dropId) {
      if (!data || !Array.isArray(data.items) || data.items.some(f => !f?.id || typeof f.name !== "string"
          || !Number.isInteger(f.saved_count) || f.saved_count < 0)
          || dropId && (data.assignment?.drop_id !== dropId
            || !Array.isArray(data.assignment.folder_ids) || data.assignment.folder_ids.length > 50
            || new Set(data.assignment.folder_ids).size !== data.assignment.folder_ids.length
            || data.assignment.folder_ids.some(id => !data.items.some(f => f.id === id)))) {
        throw new Error("Folder details could not be verified. Refresh folders.");
      }
      return data;
    }
    function apply(data) {
      model = data; listError = "";
      const selected = hooks.selection();
      if (selected && selected !== "none" && !model.items.some(f => f.id === selected)) hooks.select(null);
      paint();
    }
    function syncIdentity() {
      if (identity === owner() && accountScope === scope() && accountCsrf === csrf()) return false;
      identity = owner(); accountScope = scope(); accountCsrf = csrf();
      ++epoch; ++request; ++mutationRevision; writes.clear(); refreshPending = false;
      model = {items:[]}; loading = false; listError = "";
      hooks.resetSelection();
      close(); el.list.replaceChildren(); el.name.value = ""; el.subject.textContent = "";
      el.error.textContent = ""; el.status.textContent = ""; el["confirm-text"].textContent = ""; paint(); return true;
    }
    function paint() {
      const focused = [...strip.children].includes(doc.activeElement)
        ? doc.activeElement.getAttribute("data-folder-focus") : null;
      strip.hidden = !owner() || !hooks.visible(); strip.replaceChildren();
      if (strip.hidden) return;
      for (const entry of [{id:null,name:"All saved"}, ...model.items.map(f => ({id:f.id,name:`${f.name} (${f.saved_count})`})), {id:"none",name:"Unfiled"}]) {
        const item = button(entry.name, () => { hooks.select(entry.id); paint(); }, "save-folder-chip");
        item.setAttribute("data-folder-focus", entry.id || "all");
        const selected = (hooks.selection() || null) === entry.id;
        item.setAttribute("aria-pressed", String(selected)); item.classList.toggle("is-active", selected);
        strip.append(item);
      }
      const manage = button("Manage folders", event => show(null, event.currentTarget), "save-folder-chip saved-folder-manage");
      manage.setAttribute("data-folder-focus", "manage"); strip.append(manage);
      if (loading) strip.append(make("span", "Loading folders…", "metric-note"));
      if (listError) {
        const error = make("span", listError, "form-error"); error.setAttribute("role", "status");
        strip.append(error, button("Refresh folders", refresh, "save-folder-chip"));
      }
      if (focused) ([...strip.children].find(node => node.getAttribute("data-folder-focus") === focused) || manage).focus();
    }
    async function refresh() {
      syncIdentity(); if (!owner()) { paint(); return; }
      // One current dialog owns its assignment and the shared folder model together.
      if (current(session)) return load(session, true);
      const ticket = ++request, revision = mutationRevision;
      const account = {owner:owner(), scope:scope(), csrf:csrf(), epoch};
      const live = () => ticket === request && revision === mutationRevision
        && !writes.size && accountCurrent(account);
      loading = true; paint();
      if (writes.size) { refreshPending = true; return; }
      refreshPending = false;
      try {
        const data = validate(await hooks.api("/api/saves/folders"));
        if (live()) apply(data);
      } catch (error) { if (live()) listError = error.message || "Folders could not load."; }
      finally { if (live()) { loading = false; paint(); } }
    }
    function render(s) {
      if (!current(s)) return;
      el.title.textContent = s.drop ? "Save to folders" : "Manage saved folders";
      el.subject.textContent = s.drop ? s.drop.title || "Saved post" : "Folders are private to this profile.";
      el.status.textContent = s.pending ? "Loading…" : s.notice || (s.drop ? "Choose one or more folders. Clear every box to keep this post unfiled." : "Deleting a folder keeps its saved posts.");
      el.apply.hidden = !s.drop; el.apply.disabled = !writable(s) || !hasChanges(s);
      el.error.textContent = s.error || ""; el.error.hidden = !s.error;
      el.refresh.disabled = s.pending;
      el.submit.textContent = s.editId ? "Save name" : "Create folder";
      el.submit.disabled = !writable(s) || !s.editId && model.items.length >= 50;
      el.name.disabled = !writable(s);
      el["cancel-edit"].hidden = !s.editId;
      el["cancel-edit"].disabled = s.pending;
      el.confirmation.hidden = !s.deleteId;
      el["confirm-text"].textContent = s.deleteId ? `Delete “${model.items.find(f => f.id === s.deleteId)?.name || "this folder"}”? Its posts will stay saved.` : "";
      el.delete.disabled = !writable(s); el.keep.disabled = s.pending;
      el.list.replaceChildren(); s.controls = new Map();
      if (!s.ready) return;
      for (const folder of model.items) {
        const row = make("div", "", "saved-folder-row");
        if (s.drop) {
          const label = make("label", "", "saved-folder-choice"), pick = make("input");
          pick.type = "checkbox"; pick.checked = s.folderIds.has(folder.id); pick.disabled = !writable(s);
          s.controls.set("pick:" + folder.id, pick);
          pick.addEventListener("change", () => {
            if (!writable(s)) return;
            if (pick.checked) s.folderIds.add(folder.id); else s.folderIds.delete(folder.id);
            el.apply.disabled = !hasChanges(s);
          });
          label.append(pick, make("span", `${folder.name} (${folder.saved_count})`)); row.append(label);
        } else {
          row.append(make("span", `${folder.name} (${folder.saved_count})`, "saved-folder-label"));
          const rename = button("Rename", () => {
            if (!writable(s)) return; s.editId = folder.id; s.deleteId = null; s.error = "";
            el.name.value = folder.name; render(s); el.name.focus(); el.name.select();
          });
          rename.setAttribute("aria-label", `Rename ${folder.name}`); s.controls.set("rename:" + folder.id, rename);
          const remove = button("Delete", () => {
            if (!writable(s)) return; s.deleteId = folder.id; render(s); el.delete.focus();
          });
          remove.setAttribute("aria-label", `Delete ${folder.name}`); s.controls.set("delete:" + folder.id, remove);
          rename.disabled = remove.disabled = !writable(s); row.append(rename, remove);
        }
        el.list.append(row);
      }
      if (!model.items.length) el.list.append(make("p", "No folders yet. Create one below.", "metric-note"));
      if (model.items.length >= 50) el.list.append(make("p", "You have reached the limit of 50 folders.", "metric-note"));
    }
    function changes(s) {
      return {add_folder_ids:[...(s.folderIds || [])].filter(id => !s.initialIds?.has(id)),
        remove_folder_ids:[...(s.initialIds || [])].filter(id => !s.folderIds?.has(id))};
    }
    function hasChanges(s) {const delta = changes(s); return delta.add_folder_ids.length + delta.remove_folder_ids.length > 0;}
    async function load(s, preserveDraft = false) {
      if (!current(s)) return;
      // A replacement opening cannot edit a snapshot from before an unsettled write.
      if (s.write) { s.waitingRead = {preserveDraft}; return; }
      const draft = preserveDraft ? changes(s) : null;
      const focused = doc.activeElement;
      const focusKey = [...(s.controls || [])].find(([, node]) => node === focused)?.[0];
      const ticket = (s.read || 0) + 1; s.read = ticket;
      s.pending = true; s.ready = false; s.error = "";
      s.waitingRead = writes.size ? {preserveDraft} : null;
      s.readBusy = !s.waitingRead; render(s);
      if (s.waitingRead) return;
      const revision = mutationRevision;
      const ownsRead = () => current(s) && s.read === ticket;
      const live = () => ownsRead() && revision === mutationRevision && !writes.size;
      refreshPending = false;
      try {
        const data = validate(await hooks.api("/api/saves/folders" + (s.drop ? `?drop_id=${encodeURIComponent(s.drop.id)}` : "")), s.drop?.id);
        if (!live()) return;
        ++request; loading = false; apply(data);
        const ids = new Set(model.items.map(folder => folder.id));
        s.initialIds = new Set(data.assignment?.folder_ids || []); s.folderIds = new Set(s.initialIds);
        if (draft) {
          for (const id of draft.add_folder_ids) if (ids.has(id)) s.folderIds.add(id);
          for (const id of draft.remove_folder_ids) if (ids.has(id)) s.folderIds.delete(id);
        }
        if (s.editId && !ids.has(s.editId)) {
          s.editId = null; el.name.value = ""; s.notice = "That folder is no longer available.";
        }
        if (s.deleteId && !ids.has(s.deleteId)) {
          s.deleteId = null; s.notice = "That folder is no longer available.";
        }
        s.ready = true; s.unconfirmed = false;
      } catch (error) { if (live()) s.error = error.message || "Folders could not load."; }
      finally {
        if (ownsRead()) {
          s.readBusy = false; s.pending = Boolean(s.write || s.waitingRead); render(s);
          // Restore only this read's displaced focus, never a newly focused destination.
          if (s.ready && !s.pending && (doc.activeElement === focused || doc.activeElement === doc.body)) {
            const target = focusKey ? s.controls.get(focusKey) || el.name : focused;
            if (target?.isConnected && !target.disabled) target.focus({preventScroll:true});
          }
        }
      }
    }
    function show(drop = null, anchor = null) {
      syncIdentity(); if (!owner() || !hooks.canWrite()) { hooks.signIn(); return; }
      // A new opening retires prior dialog work; confirmed writes keep account ownership.
      session = {owner:owner(), scope:scope(), csrf:csrf(), epoch, drop:drop ? {id:drop.id,title:drop.title} : null, anchor,
        pending:false, ready:false, editId:null, deleteId:null, unconfirmed:false};
      el.name.value = ""; hooks.openDialog(el.dialog); render(session); void load(session);
    }
    function close() {
      if (el.dialog.open) el.dialog.close();
      finishClose();
    }
    function releaseWrite(s, operation) {
      if (operation.released) return;
      operation.released = true;
      if (s.write === operation) s.write = null;
      if (!accountCurrent(s)) return;
      writes.delete(operation); ++mutationRevision;
    }
    function resumeReads(confirmed) {
      if (writes.size) return;
      if (current(session)) {
        if (!session.write) void load(session, session.waitingRead?.preserveDraft ?? true);
      } else if (confirmed || refreshPending) void refresh();
    }
    async function mutate(s, path, options, notice, finish = false) {
      if (!writable(s) || writes.size) return;
      const operation = {released:false}; s.write = operation; s.operation = operation;
      writes.add(operation); ++mutationRevision;
      s.pending = true; s.error = ""; s.notice = ""; ++request; loading = false; render(s); paint();
      try {
        await hooks.api(path, {...options, csrf:true});
        if (!accountCurrent(s)) return;
        releaseWrite(s, operation);
        // A confirmed write changes Saved even after its originating dialog closes.
        hooks.changed();
        if (!accountCurrent(s)) return;
        if (!current(s)) { resumeReads(true); return; }
        s.editId = null; s.deleteId = null; el.name.value = "";
        if (finish) { close(); resumeReads(true); return; }
        s.notice = notice; await load(s, true);
      } catch (error) {
        if (!accountCurrent(s)) return;
        releaseWrite(s, operation);
        if (!current(s)) { resumeReads(false); return; }
        s.waitingRead = null;
        s.unconfirmed = !error.status || error.status >= 500;
        s.error = s.unconfirmed ? "We couldn't confirm this change. Refresh folders before trying again." : error.message;
        if (error.status === 404) s.ready = false;
      } finally {
        releaseWrite(s, operation);
        if (current(s) && s.operation === operation) {
          s.operation = null; s.pending = Boolean(s.write || s.readBusy || s.waitingRead); render(s);
          if (!s.pending && !s.error && s.ready) el.name.focus();
        }
      }
    }
    function assign(s) {
      if (s.drop) return mutate(s, `/api/saves/folders/assign/${encodeURIComponent(s.drop.id)}`,
        {method:"PATCH", json:changes(s)}, "", true);
    }
    el.apply.addEventListener("click", () => {if (writable(session) && hasChanges(session)) void assign(session);});
    el.form.addEventListener("submit", event => {
      event.preventDefault(); const s = session; if (!writable(s)) return;
      const name = el.name.value.trim().replace(/\s+/g, " ");
      if (!name) { s.error = "Give the folder a name."; render(s); el.name.focus(); return; }
      void mutate(s, "/api/saves/folders" + (s.editId ? `/${encodeURIComponent(s.editId)}` : ""),
        {method:s.editId ? "PATCH" : "POST", json:{name}}, s.editId ? "Folder renamed." : s.drop ? "Folder created. Choose it above, then save your folders." : "Folder created.");
    });
    el["cancel-edit"].addEventListener("click", () => {
      const s = session; if (!current(s) || s.pending) return;
      s.editId = null; el.name.value = ""; if (!s.unconfirmed) s.error = ""; render(s); el.name.focus();
    });
    el.refresh.addEventListener("click", () => { if (current(session) && !session.pending) void load(session); });
    el.keep.addEventListener("click", () => { if (current(session) && !session.pending) {session.deleteId = null; render(session); el.name.focus();} });
    el.delete.addEventListener("click", () => {
      const s = session; if (s?.deleteId) void mutate(s, `/api/saves/folders/${encodeURIComponent(s.deleteId)}`,
        {method:"DELETE"}, "Folder deleted. Its posts are still saved.");
    });
    function finishClose() {
      if (el.dialog.open) return;
      const old = session; session = null; el.list.replaceChildren(); el.name.value = "";
      if (accountCurrent(old)) {
        if (old.anchor?.isConnected) old.anchor.focus();
        else hooks.restoreFocus?.(old.drop?.id);
      }
    }
    el.dialog.addEventListener("close", finishClose);
    return {show, close, refresh, syncIdentity, paint:() => {syncIdentity(); paint();}};
  }
  globalThis.LouderrSavedFolders = {create};
})();
